import { SupportedLocale } from '@src/cnst/translations.cnst'
import { languageContext } from '@src/context/language.context'
import classNames from 'classnames'
import { type ReactNode, useContext, useMemo } from 'react'
import styles from './FsaBadge.module.scss'

export interface FsaBadgeProps {
  theme?: FsaBadgeTheme
  hasBlueTick?: boolean
  alignment?: FsaBadgeAlignment
  isReversedForTranslations?: boolean
}

export function FsaBadge({
  theme = FsaBadgeTheme.Black,
  alignment = FsaBadgeAlignment.Horizontal,
  hasBlueTick,
  isReversedForTranslations = true,
}: FsaBadgeProps): ReactNode {
  const { locale } = useContext(languageContext)

  const copy = {
    [SupportedLocale.svSE]: 'Kvalificerad',
    [SupportedLocale.enUS]: 'Eligible',
    [SupportedLocale.ptBR]: 'Elegível para',
    [SupportedLocale.esUS]: 'Elegible para',
    [SupportedLocale.enCA]: 'Eligible',
    [SupportedLocale.enGB]: 'Eligible',
  }

  const isReversed = useMemo(() => {
    if (!isReversedForTranslations) return false

    return [SupportedLocale.ptBR, SupportedLocale.esUS].includes(locale)
  }, [isReversedForTranslations, locale])

  const hasCenteredLogo = useMemo(() => {
    return isReversed && alignment === FsaBadgeAlignment.Horizontal
  }, [isReversed, alignment])

  const fsaBadgeStyle = useMemo(() => {
    return classNames(styles.FsaBadge, {
      [styles.FsaBadge___themeBlack]: theme === FsaBadgeTheme.Black,
      [styles.FsaBadge___themeBlue]: theme === FsaBadgeTheme.Blue,
      [styles.FsaBadge___themeLight]: theme === FsaBadgeTheme.Light,
      [styles.FsaBadge___themePurple]: theme === FsaBadgeTheme.Purple,
      [styles.FsaBadge___themeLarge]: theme === FsaBadgeTheme.Large,
      [styles.FsaBadge___hasBlueTick]: hasBlueTick,
      [styles.FsaBadge___isReversed]: isReversed,
      [styles.FsaBadge___hasCenteredLogo]: hasCenteredLogo,
      [styles.FsaBadge___isVertical]: alignment === FsaBadgeAlignment.Vertical,
      [styles.FsaBadge___isCentered]: alignment === FsaBadgeAlignment.Centered,
    })
  }, [isReversed, hasCenteredLogo, hasBlueTick])

  const tagline = useMemo(() => {
    return copy[locale]
  }, [locale])

  return (
    <div className={fsaBadgeStyle}>
      <div className={styles.FsaBadge__iconContainer}>
        <svg
          className={styles.FsaBadge__icon}
          width="26"
          height="25"
          viewBox="0 0 26 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.3"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.2116 9.35944C21.4705 10.2256 21.6096 11.1422 21.6096 12.0896C21.6096 17.3691 17.29 21.6887 12.0105 21.6887C6.73105 21.6887 2.41148 17.3691 2.41148 12.0896C2.41148 6.81015 6.73105 2.49058 12.0105 2.49058C14.7039 2.49058 17.1474 3.61476 18.895 5.41707L20.5936 3.72574C18.4106 1.48641 15.3656 0.0908203 12.0105 0.0908203C5.41118 0.0908203 0.0117188 5.49028 0.0117188 12.0896C0.0117188 18.689 5.41118 24.0884 12.0105 24.0884C18.6099 24.0884 24.0093 18.689 24.0093 12.0896C24.0093 10.4629 23.6813 8.90909 23.088 7.49108L21.2116 9.35944Z"
            className={styles.FsaBadge__circle}
          />
          <path
            d="M11.8019 13.9929L7.17133 9.39398L5.59448 10.949L11.8019 17.1139L25.1274 3.87973L23.5617 2.32471L11.8019 13.9929Z"
            className={styles.FsaBadge__tick}
          />
        </svg>
      </div>
      <div className={styles.FsaBadge__text}>
        <span className={styles.FsaBadge__types}>FSA &amp; HSA</span>
        <span className={styles.FsaBadge__status}>{tagline}</span>
      </div>
    </div>
  )
}

export enum FsaBadgeTheme {
  Blue = 'Blue',
  Light = 'Light',
  Purple = 'Purple',
  Black = 'Black',
  Large = 'Large',
}

export enum FsaBadgeAlignment {
  Horizontal = 'Horizontal',
  Vertical = 'Vertical',
  Centered = 'Centered',
}
